import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'

const FTP_POLLING_INTERVAL = 5000
const DEFAULT_CHART_POLLING_INTERVAL = 3000

/**
 * Dictionary of update modes. When updater is not running, it holds a '' value.
 * @type {Object}
 */
const UPDATE_MODE = {
  conduit: 'conduit',
  firehose: 'firehose',
  polling: 'polling'
}

/**
 * Short polling re-hydrates the activity model based on time interval. This
 * function returns query param attributes to be appended to the request URL.
 * @return {Object} Key/value query params as an object.
 */
function shortPollParams (self) {
  const obj = {}

  if ((self.serviceAreaId > 0) && !self.flashOptions.isSegmented()) {
    if (self.activity.type() !== 'discourse') {
      obj.service_area_id = self.serviceAreaId
    }
  }

  if ((self.activity.type() === 'free_text_poll') || (self.activity.type() === 'discourse')) {
    obj.limit = self.flashOptions.get('responseLimit') || 25
  }

  return obj
}

function hasGQLLiveUpdate (model) {
  return model.HAS_GRAPHQL_BACKEND && typeof model.liveUpdate === 'function'
}

/**
 * Determines if the provided model must use short polling instead of Firehose.
 * @return {boolean} True indicates model needs short polling for live updates.
 */
function forceShortPolling (model, serviceAreaId) {
  const discourseShortPolling = model.type() === 'discourse' && !hasGQLLiveUpdate(model)
  const isSegmented = model.flashOptions && model.flashOptions().isSegmented()

  // Discourse and segmented polls always use short-polling
  return discourseShortPolling || isSegmented || serviceAreaId > 0
}

function shortPollModel (self, config, interval) {
  clearTimeout(self.intervalTimeout)

  // Backbone model fetch data
  const data = shortPollParams(self)
  const comparisonPollKey = self.flashOptions &&
    self.flashOptions.isSegmented() &&
    self.flashOptions.get('teamPollPermalink')

  let fetchPromise
  if (comparisonPollKey) {
    fetchPromise = self.activity.comparisons.add({
      presenter_key: comparisonPollKey
    }).fetch({ data })
  } else {
    fetchPromise = self.activity.fetch({ data })
  }

  return fetchPromise
    .fail(config.error)
    .always(() => {
      self.intervalTimeout =
        _.delay(shortPollModel, interval, self, config, interval)
    })
}

function handleFirehoseError (...args) {
  args.splice(0, 0, 'error')
  this.trigger.apply(this, args)
}

/*
 * Activity Updater enables/disables live updates for an activity either through
 * short polling or Firehose based on varying circumstances. This allows
 * consumers of activities to enable live updates with this common interface.
 * @param {Object} options
 * @param {Backbone.Model} options.activity Activity model
 * @param {string} [options.mode='firehose'] Updater attempts to use
 *    this mode whenever starting to get live updates.
 * @param {number} [options.serviceAreaId=null] Service area to use when polling
 *    json data.
 * @param {boolean} [options.multiplex=true] Use multiplex firehose connections.
 * @event connect Connected
 * @event disconnect Disconnected
 * @event error Error occurred
 */
export default class ActivityUpdater {
  constructor (options = {}) {
    _.extend(this, Backbone.Events)

    this.options = _.defaults(options, {
      mode: UPDATE_MODE.firehose,
      serviceAreaId: null,
      multiplex: true
    })

    /**
     * Holds the current mode of the updater, one of three values:
     * 'firehose', 'polling' or '' for when updater is stopped.
     * @type {string}
     */
    this.currentMode = ''

    this.flashOptions = typeof this.activity.flashOptions === 'function'
      ? this.activity.flashOptions()
      : undefined
  }

  get activity () { return this.options.activity }

  get mode () { return this.currentMode }

  get multiplex () { return this.options.multiplex }

  get serviceAreaId () { return this.options.serviceAreaId }

  /**
   * Unsubscribe from Firehose live updates.
   * @return {Promise}
   */
  stop () {
    if (this.activity.type() === 'survey') { return }

    this.currentMode = ''
    clearTimeout(this.intervalTimeout)

    if (hasGQLLiveUpdate(this.activity)) {
      return this.activity.stopLiveUpdate()
    } else {
      return this.activity.instance.stop()
    }
  }

  /**
   * Subscribe to Firehose live updates or short polling based on conditions.
   * @return {Promise}
   */
  start () {
    if (this.activity.type() === 'survey') { return }

    const config = {
      multiplex: this.multiplex,
      connected: (...args) => {
        args.splice(0, 0, 'connect')
        return this.trigger.apply(this, args)
      },

      disconnected: (...args) => {
        args.splice(0, 0, 'disconnect')
        this.trigger.apply(this, args)
        handleFirehoseError.call(this, ...args)
      },

      error: handleFirehoseError.bind(this),

      failed: (...args) => {
        /*
         * With the default failed handler from the Firehose client
         * we just throw a string saying that we couldn't connect
         * Let's downgrade from `error` to `info` and try to provide a bit
         * more context. If we find that they're important we can upgrade
         * back to `error`
         */
        return this.trigger('error', 'Connection failed',
          { activity: this.activity.attributes })
      }
    }

    const useShortPolling = (this.options.mode === UPDATE_MODE.polling) ||
      forceShortPolling(this.activity, this.serviceAreaId)
    if (useShortPolling) {
      const interval = this.activity.type() === 'free_text_poll'
        ? FTP_POLLING_INTERVAL
        : DEFAULT_CHART_POLLING_INTERVAL

      this.currentMode = UPDATE_MODE.polling
      return shortPollModel(this, config, interval).then(config.connected)
    } else if (hasGQLLiveUpdate(this.activity)) {
      this.currentMode = UPDATE_MODE.conduit
      return this.activity.liveUpdate(config)
    } else {
      // Establish connection to Firehose
      this.currentMode = UPDATE_MODE.firehose
      return this.activity.instance.start(config)
    }
  }
}
